import { useMutation } from '@tanstack/react-query'

import { DrawerState, ProjectUpdateType } from './project-drawer'

import { useQueryClient } from '@tanstack/react-query'
import Button from 'components/core/button'
import { Project } from 'interfaces/project'
import { Ticket } from 'interfaces/ticket'
import { request } from 'lib/api/fetch-api'
import IconButton from 'lib/components/buttons/icon-button'
import { Drawer } from 'lib/components/drawer/drawer'
import { ArrowLeftIcon } from 'lucide-react'
import { toast } from 'sonner'

interface RemoveDeliverablesContentProps {
  project: Project
  setDrawerState: (state: DrawerState) => void
  setOpen: (open: boolean) => void
  deliverablesToRemove: Ticket[]
  setDeliverablesToRemove: (deliverables: Ticket[]) => void
  onUpdate: (project: Project, action: ProjectUpdateType) => void
}

export default function RemoveDeliverablesContent({
  project,
  setDrawerState,
  setOpen,
  deliverablesToRemove,
  setDeliverablesToRemove,
  onUpdate,
}: RemoveDeliverablesContentProps) {
  const queryClient = useQueryClient()

  const handleCancel = () => {
    setDeliverablesToRemove([])
    setDrawerState('details')
  }

  const handleRemoveMutation = useMutation({
    mutationFn: () => {
      return request({
        endpoint: 'removeTicketsFromProject',
        query: { id: project.id },
        body: { ticket_ids: deliverablesToRemove.map((d) => Number(d.id)) },
      })
    },
    onSuccess: () => {
      toast.success('Requests removed')
      queryClient.invalidateQueries({ queryKey: ['projectDeliverables', project.id] })
      setDeliverablesToRemove([])
      setDrawerState('details')
      onUpdate(project, ProjectUpdateType.UPDATE)
    },
    onError: (error) => {
      toast.error('Failed to remove requests')
      console.error('Failed to remove requests', error)
    },
  })

  const handleRemove = () => {
    handleRemoveMutation.mutate()
  }

  const firstDeliverableName = `"${deliverablesToRemove[0]?.subject || `Untitled ${deliverablesToRemove[0]?.skillName}`}"`

  const deliverableText = () => {
    if (deliverablesToRemove.length > 1) {
      return (
        <span className="tw-font-semibold">
          {firstDeliverableName}
          <span className="tw-font-normal"> + {deliverablesToRemove.length - 1} more requests</span>
        </span>
      )
    }
    return <span className="tw-font-semibold">{firstDeliverableName}</span>
  }

  return (
    <>
      <Drawer.Header setOpen={setOpen} showCloseButton={false} className="tw-static">
        <div className="tw-flex tw-items-center tw-gap-4">
          <IconButton
            size="xs"
            color="transparent"
            onClick={() => setDrawerState('details')}
            className="tw-mr-2"
            data-testid="BackButton"
          >
            <ArrowLeftIcon className="tw-text-neutral-700" />
          </IconButton>
          <h3 className="tw-m-0 tw-p-0">Remove Request(s) from Project?</h3>
        </div>
      </Drawer.Header>
      <Drawer.Body className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-overflow-y-auto">
        <h5 className="tw-text-normal tw-m-0 tw-break-all tw-p-0 tw-py-4 tw-font-normal">
          Are you sure you want to remove {deliverableText()} from the{' '}
          <span className="tw-font-semibold">&quot;{project.name}&quot;</span>?
        </h5>
        <p className="tw-text-md tw-m-0 tw-p-0">
          This will leave the requests as standalone requests without any project association.
        </p>
        <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
          <img src="/images/pickle_remove_deliverables_warning.png" alt="Remove ticket" className="tw-w-1/2" />
        </div>
        <div className="tw-flex tw-w-full tw-justify-end tw-gap-2 tw-bg-white tw-py-4">
          <Button onClick={handleCancel} color="lightGray">
            Cancel
          </Button>
          <Button onClick={handleRemove} color="red">
            Remove requests
          </Button>
        </div>
      </Drawer.Body>
    </>
  )
}
